/* eslint-disable import/no-anonymous-default-export */

import { convertDateStringsToTimestamps } from "../../../../db/schema/schemaHooks";
import { createId, isValidCuid2 } from "../../../../utils/cuid2Utils";

/**
 * database configuration
 * ----------------------------------------
 * Format matches feature config modules so it is auto-collected by your existing
 * Webpack loader (require.context) alongside other feature configs.
 *
 * Version semantics (store.object.data): "MAJOR.MINOR.PATCH"
 * - MAJOR (store): Dexie store definition change → requires DB version bump.
 * - MINOR (object): object model changes → coerce existing records.
 * - PATCH (data): data-only adjustments (upserts) → no schema change.
 */

const VERSION = "1.1.1";

/**
 * Default store object model for Tales.
 *
 *  * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelStore - represents the store and may be the parent of all data objects.
 *
 * @property {string} id - Unique identifier for the store. Must be set to the store's name.
 * @property {string} label -Human-readable label for the store which may be used in UI
 * @property {string} type - Type identifier for the store
 * @property {string} description -Longer description or help text explaining the purpose of the store.
 * @property {Array.<{id:number,label:string,order:number}>} categories
 *   Array of category objects available to this store. Each category has:
 *     - id {number}: numeric category identifier
 *     - label {string}: human-readable category label
 *     - order {number}: numeric ordering index for categories
 *
 *   Categories are used to group settings and Labels are used in UI
 *
 * @property {string|null} image - URL or path to an image associated with the setting, or null.
 * @property {string} version - Semantic/version string representing the version of this store definition.
 * @property {number} created - Creation timestamp in milliseconds since the epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since the epoch (Date.now()).
 */
export const modelStore = {
  id: "", // must be set to stores name
  label: "",
  type: "",
  description: "",
  categories: [{ id: 0, label: "", order: 0 }],
  image: "",
  version: "", // version of this store
  created: Date.now(),
  modified: Date.now(),
};

/**
 * Default model for data entry.
 *
 * Update level must be MINOR for model changes to take effect
 *
 * Adding keys to data objects will occur when the key is added to this model
 * Removing keys from data objects requires using the keyPruning function in the transform hook
 *
 * @constant {Object} modelData
 *
 * @property {string} id - CUID2 Unique identifier for the setting
 * @property {string} title - Title of the tale
 * @property {number} startDateTime - Start timestamp of the tale
 * @property {number} endDateTime - End timestamp of the tale
 * @property {string} description - Description of the tale
 * @property {string} timeZone - Primary TimeZone abbreviation of the tale
 * @property {string|null} image - URL or path to an image associated with the setting, or null.
 * @property {Array.<{name: string}>} travelers a list of travelers on this tale
 * @property {number} created - Creation timestamp in milliseconds since epoch (Date.now()).
 * @property {number} modified - Last-modified timestamp in milliseconds since epoch (Date.now()).
 */
export const modelData = {
  id: "",
  title: "",
  startDateTime: "",
  endDateTime: "",
  description: "",
  timeZone: "",
  images: [],
  travelers: [],
  created: Date.now(),
  updated: Date.now(),
};

// #region
/**
 * Migration Configuration Object
 *
 * Defines a single migration step for a store, including version information,
 * schema changes, data updates, and transformation hooks. Each configuration
 * file represents one version increment (MAJOR, MINOR, or PATCH).
 *
 * @typedef {Object} MigrationConfig
 *
 * ============================================================================
 * VERSION MANAGEMENT
 * ============================================================================
 *
 * @property {string} version
 *   REQUIRED. Semantic version string (MAJOR.MINOR.PATCH).
 *   Must match the version in the config file name.
 *
 *   Versioning Rules:
 *   - New stores start at 1.1.1
 *   - PATCH (position 3): Data object key values (1.1.1 -> 1.1.2)
 *   - MINOR (position 2): Model updates (1.1.x -> 1.2.1)
 *   - MAJOR (position 1): Store schema updates (1.x.x -> 2.1.1)
 *
 *   Constraints:
 *   - Cannot skip versions (1.1.1 cannot jump to 1.1.5)
 *   - After incrementing MINOR, PATCH resets to 1
 *   - After incrementing MAJOR, both MINOR and PATCH reset to 1
 *
 *   Examples:
 *   - 1.1.1 -> 1.1.2 ✓ (patch increment)
 *   - 1.1.2 -> 1.2.1 ✓ (minor increment, patch resets)
 *   - 1.2.7 -> 2.1.1 ✓ (major increment, both reset)
 *   - 1.1.1 -> 1.1.5 ✗ (skips versions 1.1.2-1.1.4)
 *   - 1.1.2 -> 1.2.2 ✗ (patch should reset to 1)
 *
 * @property {boolean} forceUpdate
 *   Controls whether the user can skip this migration.
 *   - true: Skip user prompt, apply update automatically
 *   - false: Allow user to decline the update
 *
 *   Future Behavior:
 *   When true, will prompt user to backup before forcing the update.
 *
 * @property {string} type
 *   Human-readable update type indicator: "MAJOR" | "MINOR" | "PATCH"
 *   For convenience and quick reference only.
 *   Not used in code to determine update behavior.
 *
 * ============================================================================
 * DOCUMENTATION
 * ============================================================================
 *
 * @property {string[]} changeLog
 *   Array of specific technical changes included in this config.
 *   Intended for developer documentation and internal tracking.
 *
 *   Examples:
 *   - "Removing description index"
 *   - "Adding email field to user model"
 *   - "Migrating data from oldField to newField"
 *
 * @property {string} changeDescription
 *   User-facing description of the update.
 *   Displayed to users when prompted to apply the migration.
 *   Should be clear and non-technical.
 *
 *   Example:
 *   "This update improves search performance by optimizing the index structure."
 *
 * ============================================================================
 * STORE SCHEMA (MAJOR UPDATES)
 * ============================================================================
 *
 * @property {Object} stores
 *   Defines the IndexedDB store schema and indexes.
 *
 *   Rules:
 *   - Only ONE store definition per config file
 *   - Additional stores will be ignored
 *   - Store modifications require a MAJOR version bump
 *   - Modifying indexes (add/remove) requires a new MAJOR update config
 *
 *   Format:
 *   The key is the store name, the value is a comma-separated string of indexes.
 *   First index is the keyPath, remaining are indexed fields.
 *
 *   @example
 *   stores: {
 *     settings: "id,modified,category"
 *     // "id" = keyPath
 *     // "modified" and "category" = indexed fields for queries
 *   }
 *
 *   Index Modifications:
 *   - Add index: Include new index in string, create MAJOR config
 *   - Remove index: Exclude index from string, create MAJOR config
 *
 * ============================================================================
 * MODEL UPDATES (MINOR UPDATES)
 * ============================================================================
 *
 * @property {Object} model
 *   Defines the data models for store objects and data objects.
 *
 * @property {Object} model.storeObject
 *   Model for the single store metadata object.
 *   This is the special object with id "__history" that tracks store state.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model, create MINOR config
 *
 * @property {Object} model.dataObject
 *   Model for all data objects in the store (excluding storeObject).
 *   All data rows will conform to this model.
 *
 *   Model Modifications:
 *   - Add keys: Include new key in model, create MINOR config
 *   - Remove keys: Remove key from model AND configure transform hook
 *     with keyPruning to remove the keys from existing data
 *     (keys are not removed automatically)
 *
 *   @example
 *   model: {
 *     storeObject: {
 *       id: "__history",
 *       version: "1.2.1",
 *       created: Date.now(),
 *       modified: Date.now()
 *     },
 *     dataObject: {
 *       id: "",
 *       name: "",
 *       email: "", // newly added field
 *       created: Date.now(),
 *       modified: Date.now()
 *     }
 *   }
 *
 * ============================================================================
 * DATA POPULATION (PATCH UPDATES)
 * ============================================================================
 *
 * @property {Array} data
 *   Array of data objects to add to the store during this update.
 *   Empty array if no data needs to be added.
 *
 *   Initial Store Setup (v1.1.1):
 *   - Optionally seed the storeObject to lock starting versions
 *   - If not seeded, fallback creates object from model with version
 *     and timestamps
 *
 *   Each object in the array should conform to the dataObject model.
 *
 *   @example
 *   data: [
 *     {
 *       id: "default-1",
 *       name: "Default Setting",
 *       value: true,
 *       created: 1699564800000,
 *       modified: 1699564800000
 *     }
 *   ]
 *
 * ============================================================================
 * TRANSFORMATION HOOKS
 * ============================================================================
 *
 * @property {Object} hooks
 *   Optional transformation and lifecycle hooks.
 *   Hooks apply to data objects only, never to the storeObject/meta row.
 *
 * @property {Function} [hooks.transform]
 *   Transforms each data object during migration.
 *
 *   @param {Object} obj - The data object being transformed
 *   @param {Object} ctx - Context object
 *   @param {string} ctx.stepVersion - The version of this migration step
 *   @param {Object} ctx.model - The target dataObject model
 *   @param {Object} ctx.utils - Utility functions
 *   @param {Function} ctx.utils.keyPruning - Removes keys not in model
 *   @returns {Object} The transformed object
 *
 *   Common Use Cases:
 *   - Remove deprecated fields with keyPruning
 *   - Transform field values
 *   - Migrate data from old structure to new
 *
 *   @example
 *   transform: (obj, ctx) => {
 *     console.debug(`[settings@${ctx.stepVersion}] transform`, Object.keys(obj));
 *
 *     // Remove fields no longer in the model
 *     ctx.utils.keyPruning(obj, ctx.model);
 *
 *     // Transform a field
 *     if (obj.oldField) {
 *       obj.newField = obj.oldField.toUpperCase();
 *       delete obj.oldField;
 *     }
 *
 *     return obj;
 *   }
 *
 * @property {Function} [hooks.beforePopulate]
 *   Runs before data population begins.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Prepare data structures
 *   - Validate preconditions
 *   - Set up temporary state
 *
 * @property {Function} [hooks.afterPopulate]
 *   Runs after data population completes.
 *
 *   @param {Object} params
 *   @param {IDBDatabase} params.db - IndexedDB database instance
 *   @param {string} params.store - Store name
 *   @param {string} params.version - Migration version
 *   @returns {Promise<void>}
 *
 *   Use Cases:
 *   - Clean up temporary data
 *   - Trigger post-migration actions
 *   - Validate migration success
 */
// #endregion
export default {
  /**
   * version  MAJOR.MINOR.PATCH
   *
   * For new store, set version at 1.1.1
   *
   * To apply patch data object key values, increment position 3 (1.1.2, so 1.1.1 -> 1.1.2)
   * To apply minor "model" updates, increment position 2 (1.2.1, so 1.1.1 || 1.1.2 || 1.1.6 -> 1.2.1)
   * To apply MAJOR "store" updates, increment postion 1 (2.1.1, so 1.2.1 || 1.2.7 || 1.9.4 -> 2.1.1)
   *
   * Versions cannot jump sequence
   *   (e.g. ver 1.1.1 cannot jump to 1.1.5, ver 1.1.2 cannot jump to 1.2.2, it must go to 1.2.1)
   *
   * @property {string} version - REQUIRED Version used in update and must match version in file name
   */
  version: VERSION,

  /**
   * forceUpdate
   *
   * to be used when updates are required not optional by user
   *
   * true  this will skip the user prompt
   * false this will allow user to skip update
   *
   * ** FUTURE behavior will be to prompt user to backup first and then force the update
   *
   * @property {boolean} forceUpdate - force update without allowing user to skip
   */
  forceUpdate: false,

  /**
   * type
   *
   * Type options (MAJOR || MINOR || PATCH)
   *
   * This value is for convenience and quick reference and is not used to determine any update decisions in code
   *
   * @property {string} type - Human-readable indication representing the version number update type MAJOR.MINOR.PATCH
   * */
  type: "MAJOR",

  /**
   * changeLog
   *
   * Intended for documentation purposes but could be used for other purposes as well
   *
   * @property {string[]} changeLog - Array of specific changes this config contains
   */
  changeLog: [
    "- create store and indexes",
    "- establish store and data models",
    "- populate store with initial settings",
  ],

  /**
   * changeDescription
   *
   * Description of changes in this config update. Intended to be informative to app user
   *  if prompted to update
   *
   * @property {string} changeDescription - Public description of update
   */
  changeDescription: "Initiate Tales store as part of TOAT feature.",

  /**
   * stores (MAJOR)
   *
   * ONLY supports a single store defined per config, new store = new config file
   *  additional configured stores will be ignored
   *
   * Store modifications require a database version bump
   *
   *  + to modify indexes (adding), add index configuration, create a MAJOR update config file
   *  - to modify indexes (removing), remove index, leaving other indexes in place, crate MAJOR update config file
   *
   * @constant {Object} stores Accepts only one store and schema definition
   */
  stores: {
    tales: "id,title,startDateTime,endDateTime",
  },

  /**
   * Models (MINOR)
   *
   * Minor updates will make configured change to ALL data objects
   * For storeObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys
   *
   * For dataObject
   *  + to modify model (adding keys), add key and create a MINOR update config file
   *  - to modify model (removing keys), remove key leaving other keys and configure
   *    transform hook to run keyPruning. Keys are not removed automatically
   *
   * @constant {Object} storeObject Defines model for single store object
   * @constant {Object} dataObject Defines model for all data objects
   */
  model: {
    storeObject: modelStore,
    dataObject: modelData,
  },

  // not implemented yet but will allow update policies to be defined.
  dataPolicies: {
    id: {
      generate: "keep", // keep || all ||  invalid (default=keep)
      //   action: ({ store, index }) => `${store}-${index + 1}`,
    },
  },
  /**
   * data (PATCH)
   *
   * When store is initiated (v1.1.1)
   * - Seed the storeObject row in data for this store if you want to lock their starting versions.
   * - Fallback will create an object using model and will only apply version and date/time stamps
   *
   * @property {Array.<{...dataObject}>} data define data items to add to store for this patch update
   *    if no data needs to be added, keep array empty
   */
  data: [
    // The store's own meta row (id must equal the store name)
    {
      id: "tales", // must be set to stores name
      label: "Tales",
      type: "feature",
      description: "Store for Tales in feature.",
      categories: [],
      image: "",
      version: VERSION,
      created: Date.now(),
      modified: Date.now(),
    },
    {
      title: "Costa Rica 2024",
      startDateTime: "2024-02-06T12:30:00.000Z",
      endDateTime: "2024-02-17T03:30:00.000Z",
      description:
        "Our second trip to Costa Rica. Plan to visit a part of inland CR, rain forests, be pampered in Punta Islita and make some overnight stops.",
      timeZone: "America/Costa_Rica",
      images: [],
      travelers: [],
      id: "rwkntocyn9q73xuwux5aqd0g", //170663573984364400  // "rwkntocyn9q73xuwux5aqd0g"
      created: 1707167500816,
      updated: 1749269048039,
    },
    {
      title: "San Juan Islands 2025",
      startDateTime: "2025-06-08T15:00:00.000Z",
      endDateTime: "2025-06-14T23:00:00.000Z",
      description:
        "Our first trip to the islands. Looking forward to a relaxing time and hopefully see some whales.",
      timeZone: "America/Los_Angeles",
      images: [],
      travelers: [],
      id: "v8gj7ejto9iujvkiob8q3g8u", // 174732227961933630  // v8gj7ejto9iujvkiob8q3g8u
      created: 1749086632765,
      updated: 1749831141888,
    },
    {
      title: "Florida 2025",
      startDateTime: "2025-10-25T13:00-05:00",
      endDateTime: "2025-11-01T23:00-05:00",
      description:
        "Makin' time for family. Relaxing at the beach and soaking up the sun.",
      timeZone: "America/Chicago",
      images: [],
      travelers: [],
      id: "a3y4oi0kssjimi59mba751r1", // 176037165098612100 // a3y4oi0kssjimi59mba751r1
      created: 1760385101752,
      updated: 1760996679276,
    },
    {
      title: "Florida 2025 - TEST",
      startDateTime: "2025-11-25T18:00:00.000Z",
      endDateTime: "2025-12-02T04:00:00.000Z",
      description:
        "Makin' time for family. Relaxing at the beach and soaking up the sun.",
      timeZone: "America/Chicago",
      images: [],
      travelers: [],
      id: "a3y4oi0kssjimi59mba751r1", // 176037165098612100 // a3y4oi0kssjimi59mba751r1
      created: 1760385101752,
      updated: 1760996679276,
    },
  ],

  /**
   * Hooks
   *
   * - hooks are for data objects and do not apply to storeObjects
   */
  hooks: {
    // Will run for *data rows only* (never for the storeObject/meta row)
    // transform: (obj, ctx) => {
    //   console.debug(
    //     `[settings@${ctx.stepVersion}] transform`,
    //     Object.keys(obj)
    //   );
    //   // Only prune in *this* version, e.g. when you removed fields
    //   ctx.utils.keyPruning(obj, ctx.model);
    //   return obj;
    // },
    // beforePopulate: async ({ db, store, version, cfg}) => {},
    // afterPopulate: async ({ db, store, version, cfg }) => {},
    // beforePopulate: async ({ db, store, version, cfg }) => {
    // const generate = cfg?.dataPolicies?.id?.generate || null;
    // const shouldGenerateAll = generate === "all";
    // const shouldGenerateInvalid = generate === "invalid";
    // const data = cfg.data;
    // const dataLength = data.length;
    // for (let i = 0; i < dataLength; i++) {
    //   const rec = data[i];
    //   if (data[i].id === cfg.store) continue;
    //   if (shouldGenerateAll) {
    //     rec.id = createId();
    //   } else if (shouldGenerateInvalid && !isValidCuid2(rec.id)) {
    //     rec.id = createId();
    //   }
    //   // convertDateStringsToTimestamps(rec);
    // }
    // If `store` is a store config object with a name:
    //   const tableName =
    //     typeof store === "string" ? store : store.name || store.storeName;
    //   const table = db.table(tableName);
    // Grab all rows that were just populated
    //   const rows = await table.toArray();
    //   cfg.data.forEach((rec, index) => {
    //     const generate = cfg?.dataPolicies?.id?.mode || null;
    //     // apply policies
    //     if (generate && generate === "all") rec.id = createId();
    //     else if (generate && generate === "invalid") {
    //       if (isValidCuid2(rec.id)) {
    //         rec.id = createId();
    //       }
    //     }
    //     //   for (const row of rows) {
    //     // This mutates `rec` in-place, converting any "YYYY-MM-DD" strings
    //     // in any nested object/array to timestamps in milliseconds.
    //     convertDateStringsToTimestamps(rec);
    //     //   }
    //     // Persist the converted rows back to IndexedDB
    //     //   if (rows.length > 0) {
    //     //     await table.bulkPut(rows);
    //     //   }
    //   });
    // },
  },
};

/* eslint-enable import/no-anonymous-default-export */
